import { Project } from '@bulthaup/api/db';
import { Button, Divider, Group, Stack, Text, Tooltip } from '@mantine/core';

import { useBooleanButtonState } from './-hooks/-use-boolean-button-state';

type BooleanField =
  | 'isWorktopProviderNotified'
  | 'isSplashbackProviderNotified'
  | 'isInstallationFolderReady';

type ProviderNotificationSectionProps = {
  project: Project;
  hasWorktop: boolean;
  hasSplashback: boolean;
};

export function ProviderNotificationSection({
  project,
  hasWorktop,
  hasSplashback,
}: ProviderNotificationSectionProps) {
  const getCommonProviderRequirements = () => {
    const missingRequirements: string[] = [];

    if (!project.deliveryDate) {
      missingRequirements.push('la date de livraison doit être documentée');
    }

    if (!project.installerId) {
      missingRequirements.push('un poseur doit être sélectionné');
    }

    return missingRequirements;
  };

  const { isFieldPending, toggleBooleanState } =
    useBooleanButtonState<BooleanField>({
      project,
      fieldConfigs: {
        isWorktopProviderNotified: {
          successMessage: 'État fournisseur plan de travail mis à jour',
          getBlockingCondition: () => {
            const requirements = getCommonProviderRequirements();
            if (requirements.length === 0) {
              return null;
            }

            return {
              title: "Avant d'informer le fournisseur vous devez :",
              requirements,
            };
          },
        },
        isSplashbackProviderNotified: {
          successMessage: 'État fournisseur crédence mis à jour',
          getBlockingCondition: () => {
            const requirements = getCommonProviderRequirements();
            if (requirements.length === 0) {
              return null;
            }

            return {
              title: "Avant d'informer le fournisseur vous devez :",
              requirements,
            };
          },
        },
        isInstallationFolderReady: {
          successMessage: 'État dossier poseur mis à jour',
          getBlockingCondition: () => {
            const requirements = getCommonProviderRequirements();
            if (requirements.length === 0) {
              return null;
            }

            return {
              title: "Avant d'informer le poseur vous devez :",
              requirements,
            };
          },
        },
      },
    });

  const isWorktopProviderNotified = hasWorktop
    ? project.isWorktopProviderNotified
    : false;

  const isSplashbackProviderNotified = hasSplashback
    ? project.isSplashbackProviderNotified
    : false;

  const isInstallationFolderReady = project.isInstallationFolderReady ?? false;
  const isWorktopNotificationDisabled =
    isFieldPending('isWorktopProviderNotified') || !hasWorktop;
  const isSplashbackNotificationDisabled =
    isFieldPending('isSplashbackProviderNotified') || !hasSplashback;

  const worktopButton = (
    <Button
      color={isWorktopProviderNotified ? 'green' : 'red'}
      variant="filled"
      onClick={() =>
        void toggleBooleanState(
          'isWorktopProviderNotified',
          isWorktopProviderNotified,
        )
      }
      disabled={isWorktopNotificationDisabled}
      loading={isFieldPending('isWorktopProviderNotified')}
      style={{ flexShrink: 0 }}
    >
      {isWorktopProviderNotified ? 'OUI' : 'NON'}
    </Button>
  );

  const splashbackButton = (
    <Button
      color={isSplashbackProviderNotified ? 'green' : 'red'}
      variant="filled"
      onClick={() =>
        void toggleBooleanState(
          'isSplashbackProviderNotified',
          isSplashbackProviderNotified,
        )
      }
      disabled={isSplashbackNotificationDisabled}
      loading={isFieldPending('isSplashbackProviderNotified')}
      style={{ flexShrink: 0 }}
    >
      {isSplashbackProviderNotified ? 'OUI' : 'NON'}
    </Button>
  );

  return (
    <Stack>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Text style={{ flex: 1, minWidth: 0 }}>Plan de travail présent</Text>
        <Text fw={700} style={{ flexShrink: 0 }}>
          {hasWorktop ? 'OUI' : 'NON'}
        </Text>
      </Group>

      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Text style={{ flex: 1, minWidth: 0 }}>
          Fournisseur plan de travail informé de la date de pose
        </Text>
        {!hasWorktop ? (
          <Tooltip label="Un plan de travail est requise.">
            {worktopButton}
          </Tooltip>
        ) : (
          worktopButton
        )}
      </Group>

      <Divider />

      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Text style={{ flex: 1, minWidth: 0 }}>Crédence présente</Text>
        <Text fw={700} style={{ flexShrink: 0 }}>
          {hasSplashback ? 'OUI' : 'NON'}
        </Text>
      </Group>

      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Text style={{ flex: 1, minWidth: 0 }}>
          Fournisseur crédence informé de la date de pose
        </Text>
        {!hasSplashback ? (
          <Tooltip label="Une crédence est requise.">
            {splashbackButton}
          </Tooltip>
        ) : (
          splashbackButton
        )}
      </Group>

      <Divider />

      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Text style={{ flex: 1, minWidth: 0 }}>
          Dossier Poseur prêt - Poseur / artisans externes informés de la date
          de pose
        </Text>
        <Button
          color={isInstallationFolderReady ? 'green' : 'red'}
          variant="filled"
          onClick={() =>
            void toggleBooleanState(
              'isInstallationFolderReady',
              isInstallationFolderReady,
            )
          }
          disabled={isFieldPending('isInstallationFolderReady')}
          loading={isFieldPending('isInstallationFolderReady')}
          style={{ flexShrink: 0 }}
        >
          {isInstallationFolderReady ? 'OUI' : 'NON'}
        </Button>
      </Group>
    </Stack>
  );
}
